{
  "blocks": [
    {
      "id": "moon-article-embryo-freezing",
      "kind": "article",
      "tags": ["moon", "moon-guides"],
      "title": "What is embryo freezing, exactly?",
      "subtitle": "The science behind preserving your future family.",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Start here",
        "quickSummary": [
          {
            "title": "Proven technology.",
            "description": "Embryo freezing has been successful since 1984, with modern vitrification achieving 95%+ thaw survival."
          },
          {
            "title": "Shared decision.",
            "description": "Embryos require consent from both the egg and sperm provider for use, storage, or disposal."
          },
          {
            "title": "No expiration.",
            "description": "Embryos can be stored indefinitely with no evidence that duration affects outcomes."
          }
        ],
        "sections": [
          {
            "title": "What is cryopreservation?",
            "content": "Embryo freezing (cryopreservation) is the process of fertilizing eggs with sperm, growing the resulting embryos to the blastocyst stage (day 5 or 6), and then vitrifying them for long-term storage. It’s one of the most well-established assisted reproductive technologies, with the first successful pregnancy from a frozen embryo reported in 1984.",
            "bulletPoints": [
              "Embryos are grown to blastocyst stage (day 5–6) before freezing",
              "Unlike egg freezing, requires a sperm source (partner or donor)",
              "Higher survival rates than frozen eggs after thawing"
            ]
          },
          {
            "title": "How fertilization works",
            "content": "Unlike egg freezing, embryo freezing requires a sperm source, either a partner or donor. The advantage is that embryos have slightly higher survival rates after thawing, and you can test them for chromosomal abnormalities (PGT-A) before deciding which to transfer."
          },
          {
            "title": "The lab process",
            "content": "The process is identical to egg freezing up through retrieval. After that, the embryology lab takes over: fertilization, culture, grading, and optional genetic testing. You’ll typically get updates from your clinic on day 1 (fertilization check), day 3 (cell division), and day 5–6 (blastocyst formation).",
            "diagram": {
              "label": "Vitrification process"
            }
          },
          {
            "title": "Modern vitrification",
            "content": "Vitrification, the flash-freezing technique used today, is a major improvement over the slow-freezing methods of the past. It prevents ice crystal formation inside cells, which is what used to damage embryos during the freezing process. Modern survival rates after thawing exceed 95% at most clinics."
          },
          {
            "title": "Legal considerations",
            "content": "One important consideration: embryos are legally considered shared property between the egg and sperm providers. This means both parties typically need to consent to their use, storage, or disposal. If you’re using a partner’s sperm, discuss what happens to the embryos in various scenarios (separation, death, disagreement) before starting."
          },
          {
            "title": "Storage & future use",
            "content": "Embryos can be stored indefinitely. There’s no evidence that storage duration affects outcomes: babies have been born from embryos frozen for over 25 years. Annual storage costs typically range from $500 to $1,000 per year."
          },
          {
            "title": "The transfer process",
            "content": "When you’re ready to use your embryos, the transfer process is much simpler than retrieval. It’s a quick procedure (about 10 minutes) that doesn’t require anesthesia. Your doctor places the embryo into your uterus using a thin catheter guided by ultrasound. Most patients return to normal activities the same day."
          }
        ],
        "references": [
          {
            "authors": "Practice Committees of ASRM and SART",
            "title": "Mature oocyte cryopreservation: a guideline. Fertility and Sterility (2013)"
          },
          {
            "authors": "Rienzi et al.",
            "title": "Oocyte, embryo and blastocyst cryopreservation in ART. Best Practice & Research Clinical Obstetrics & Gynaecology (2012)"
          }
        ],
        "relatedArticles": [
          "moon-article-cycle-process",
          "moon-article-fresh-vs-frozen-choice"
        ]
      }
    },
    {
      "id": "moon-article-cycle-process",
      "kind": "article",
      "tags": ["moon", "moon-guides"],
      "title": "What happens during a cycle",
      "subtitle": "Four phases, two weeks, one goal.",
      "readTimeMinutes": 8,
      "body": {
        "introduction": "The process",
        "quickSummary": [
          {
            "title": "10–14 days total.",
            "description": "The entire cycle from baseline to retrieval typically takes about two weeks."
          },
          {
            "title": "Four phases.",
            "description": "Baseline, stimulation, trigger, and retrieval, each with specific steps and timing."
          },
          {
            "title": "Regular monitoring.",
            "description": "Expect clinic visits every 2–3 days during stimulation for blood work and ultrasound."
          }
        ],
        "sections": [
          {
            "title": "Phase 1: Baseline",
            "content": "On day 2 or 3 of your period, you’ll visit the clinic for blood work and an ultrasound. The doctor checks your hormone levels (estradiol, FSH, LH) and counts the small follicles visible on each ovary. This baseline assessment confirms you’re ready to start stimulation."
          },
          {
            "title": "Phase 2: Stimulation",
            "content": "This is the main event: typically 8–12 days of daily hormone injections. The medications (gonadotropins like Gonal-F, Follistim, or Menopur) stimulate your ovaries to develop multiple follicles simultaneously, rather than the single egg your body would normally produce.",
            "bulletPoints": [
              "Clinic visits every 2–3 days for monitoring",
              "Blood work and transvaginal ultrasound at each visit",
              "Medication doses adjusted based on your response"
            ],
            "diagram": {
              "label": "Follicle growth timeline"
            }
          },
          {
            "title": "Preventing premature ovulation",
            "content": "You may also take a GnRH antagonist (like Cetrotide or Ganirelix) starting around day 5–6 to prevent premature ovulation. This ensures the eggs stay in the follicles until retrieval."
          },
          {
            "title": "Phase 3: Trigger",
            "content": "When the largest follicles reach approximately 18–20mm, your doctor prescribes the trigger shot. This is precisely timed (usually given at a very specific time in the evening) because retrieval must happen exactly 36 hours later. The trigger shot (hCG or Lupron) causes final egg maturation."
          },
          {
            "title": "Phase 4: Retrieval",
            "content": "This is a short outpatient procedure performed under light sedation (you’ll be asleep but won’t need general anesthesia). Using ultrasound guidance, the doctor inserts a thin needle through the vaginal wall to reach each follicle and aspirate the fluid containing the egg. The entire procedure takes 15–25 minutes."
          },
          {
            "title": "Recovery",
            "content": "Recovery is usually quick. You may feel bloated, crampy, or tired for a few days. Most people take the day of retrieval off and return to normal activities within 1–2 days. Your clinic will call you the next day with results: how many eggs were retrieved and how many were mature."
          }
        ],
        "references": [
          {
            "authors": "Practice Committee of ASRM",
            "title": "Optimizing natural fertility: a committee opinion. Fertility and Sterility (2022)"
          },
          {
            "authors": "Macklon et al.",
            "title": "The science behind 25 years of ovarian stimulation for IVF. Human Reproduction Update (2006)"
          }
        ]
      }
    },
    {
      "id": "moon-article-pgt-testing",
      "kind": "article",
      "tags": ["moon", "moon-decisions"],
      "title": "PGT testing: do we need it?",
      "subtitle": "Weighing the pros, cons, and cost of genetic testing.",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Decisions",
        "quickSummary": [
          {
            "title": "Checks for chromosomal issues.",
            "description": "PGT-A screens embryos for aneuploidy before transfer, reducing miscarriage risk."
          },
          {
            "title": "Adds $3K–$6K.",
            "description": "The test adds significant cost and requires embryo freezing while awaiting results."
          },
          {
            "title": "Most beneficial after 35.",
            "description": "The majority of embryos from patients over 37 are aneuploid, making testing most impactful."
          }
        ],
        "sections": [
          {
            "title": "What is PGT-A?",
            "content": "PGT-A (preimplantation genetic testing for aneuploidy) checks embryos for chromosomal abnormalities before transfer. It’s one of the most debated topics in reproductive medicine, and understanding the nuances helps you make an informed decision."
          },
          {
            "title": "How the biopsy works",
            "content": "The test requires a small biopsy of 5–10 cells from the trophectoderm, the outer layer of the blastocyst that becomes the placenta. This biopsy happens on day 5 or 6, and the embryo is then frozen while cells are sent to a genetics lab. Results take 1–2 weeks and classify embryos as euploid (normal chromosomes), aneuploid (abnormal), or mosaic (a mix)."
          },
          {
            "title": "The case for PGT-A",
            "content": "It can reduce the risk of failed implantation, miscarriage, and chromosomal conditions like Down syndrome. For patients over 37, the majority of embryos are aneuploid, so testing helps identify which embryos have the best chance.",
            "bulletPoints": [
              "Reduces miscarriage risk from chromosomal causes",
              "Enables confident single embryo transfer, avoiding twins",
              "Identifies mosaic embryos for informed decision-making"
            ]
          },
          {
            "title": "The case against",
            "content": "PGT-A adds $3,000–$6,000 to your cycle cost. The biopsy carries a small risk of embryo damage (though this is rare with experienced labs). There’s also the issue of mosaic embryos: some mosaic embryos can result in healthy pregnancies, but many clinics recommend against transferring them."
          },
          {
            "title": "Key limitations",
            "content": "PGT-A tests the trophectoderm, not the inner cell mass (which becomes the baby). In rare cases, the genetics of these two cell populations can differ, leading to false positives or false negatives."
          },
          {
            "title": "Who benefits most?",
            "content": "Patients over 35, those with recurrent miscarriage, previous failed transfers, or known chromosomal issues. For patients under 35 with no risk factors, the benefit is less clear: most of their embryos will be euploid anyway."
          },
          {
            "title": "Making the decision",
            "content": "Discuss with your RE whether PGT-A makes sense for your specific situation. There’s no universally right answer: it depends on your age, number of embryos, goals, and comfort level with uncertainty."
          }
        ],
        "references": [
          {
            "authors": "Munne et al.",
            "title": "Preimplantation genetic testing for aneuploidy versus morphology as selection criteria. Fertility and Sterility (2019)"
          },
          {
            "authors": "Gleicher et al.",
            "title": "A single thawed euploid embryo transfer has limited efficacy. Human Reproduction (2020)"
          }
        ],
        "relatedArticles": ["moon-article-understanding-numbers"]
      }
    },
    {
      "id": "moon-article-understanding-numbers",
      "kind": "article",
      "tags": ["moon", "moon-science"],
      "title": "Understanding your numbers",
      "subtitle": "What your blood work actually tells your doctor.",
      "readTimeMinutes": 6,
      "body": {
        "introduction": "AMH, FSH, AFC",
        "quickSummary": [
          {
            "title": "Three key tests.",
            "description": "AMH, FSH, and AFC together paint a picture of your ovarian reserve."
          },
          {
            "title": "Quantity, not quality.",
            "description": "These tests measure how many eggs you have, not how good they are. Quality correlates with age."
          },
          {
            "title": "Context matters.",
            "description": "A single number means little on its own. Your RE interprets results together with your age and history."
          }
        ],
        "sections": [
          {
            "title": "AMH (Anti-Müllerian Hormone)",
            "content": "AMH is a blood test that can be done any day of your cycle. It’s produced by the small follicles in your ovaries, so it’s a direct indicator of your egg supply. Higher AMH generally means more eggs available for stimulation.",
            "bulletPoints": [
              "Normal range: roughly 1.0–3.5 ng/mL for reproductive-age women",
              "Can be drawn any day of your cycle",
              "Higher values suggest more eggs available for stimulation"
            ],
            "diagram": {
              "label": "Ovarian reserve decline by age"
            }
          },
          {
            "title": "FSH (Follicle-Stimulating Hormone)",
            "content": "FSH is measured on day 2–3 of your cycle. It’s the hormone your brain sends to tell your ovaries to start developing eggs. Higher FSH can indicate your brain is working harder to stimulate the ovaries, a sign of diminished reserve. Ideal is under 10 mIU/mL."
          },
          {
            "title": "AFC (Antral Follicle Count)",
            "content": "AFC is done via transvaginal ultrasound, also on day 2–3. The doctor counts the small resting follicles (2–10mm) visible on each ovary. Each antral follicle potentially contains an egg that could be retrieved. A total count of 10–20 across both ovaries is considered normal."
          },
          {
            "title": "What these tests don’t tell you",
            "content": "Egg quality. There’s no direct test for quality. It’s primarily correlated with age. A 28-year-old with low AMH still typically has better egg quality than a 40-year-old with high AMH. Quality is ultimately assessed indirectly through fertilization rates and embryo development."
          },
          {
            "title": "Don’t panic over a single number",
            "content": "These tests are best interpreted together, in context of your age and medical history. AMH can fluctuate between cycles, and different labs may give different results. What matters most is the overall picture your RE puts together."
          }
        ],
        "references": [
          {
            "authors": "Practice Committee of ASRM",
            "title": "Testing and interpreting measures of ovarian reserve: a committee opinion. Fertility and Sterility (2020)"
          },
          {
            "authors": "Broer et al.",
            "title": "AMH: the gold standard for ovarian reserve testing? Human Reproduction Update (2014)"
          }
        ]
      }
    },
    {
      "id": "moon-article-pick-clinic",
      "kind": "article",
      "tags": ["moon", "moon-decisions"],
      "title": "How to pick the right clinic",
      "subtitle": "The questions most people forget to ask.",
      "readTimeMinutes": 7,
      "body": {
        "introduction": "Clinic search",
        "quickSummary": [
          {
            "title": "Look beyond success rates.",
            "description": "Raw numbers can be misleading: clinics with lower rates may take on harder cases."
          },
          {
            "title": "The lab matters most.",
            "description": "Embryology lab quality varies widely and directly impacts your outcomes."
          },
          {
            "title": "Trust your gut.",
            "description": "You should feel heard, respected, and informed by your care team."
          }
        ],
        "sections": [
          {
            "title": "Start with SART.org",
            "content": "The Society for Assisted Reproductive Technology maintains a database of success rates for every IVF clinic in the US. But raw success rates can be misleading. A clinic with lower numbers might take on harder cases, while a clinic with stellar rates might cherry-pick easier patients."
          },
          {
            "title": "Questions for your first consultation",
            "content": "Your first consultation is your chance to evaluate both the clinic and the doctor. Come prepared with specific questions that will reveal how the clinic operates and whether it’s a good fit for you.",
            "bulletPoints": [
              "What stimulation protocol would you recommend for me?",
              "How many eggs do you expect based on my numbers?",
              "What’s your cancellation rate?",
              "What’s your policy on weekend monitoring?",
              "How many embryologists are on staff?"
            ]
          },
          {
            "title": "The embryology lab",
            "content": "The embryology lab matters enormously. This is where your eggs become embryos, and lab quality varies widely. Ask about their blastocyst conversion rate, their freeze-thaw survival rate, and whether they have a dedicated lab director."
          },
          {
            "title": "Location & convenience",
            "content": "Don’t just go with the closest clinic. A clinic with higher success rates for your specific profile can mean the difference between one cycle and three. That said, convenience matters too: you’ll be visiting every 2–3 days during stimulation, so factor in travel time."
          },
          {
            "title": "Communication style",
            "content": "Some clinics assign you a dedicated nurse coordinator; others use a team approach. Some have patient portals with real-time results; others call you. Neither is objectively better, but think about what works for your communication preferences."
          },
          {
            "title": "Cost transparency",
            "content": "Cost transparency is a green flag. Good clinics provide detailed cost breakdowns upfront, including medications, monitoring, procedures, anesthesia, and storage. Be wary of clinics that are vague about pricing or surprise you with hidden fees."
          },
          {
            "title": "Trust your instincts",
            "content": "You should feel heard, respected, and informed. If a doctor dismisses your questions or rushes through your consultation, that’s a red flag, no matter how good their numbers are."
          }
        ],
        "references": [
          {
            "authors": "Society for Assisted Reproductive Technology",
            "title": "SART National Summary Report. sart.org (2023)"
          },
          {
            "authors": "Domar et al.",
            "title": "Impact of psychological factors on IVF outcomes. Human Reproduction (2018)"
          }
        ]
      }
    },
    {
      "id": "moon-article-supplements",
      "kind": "article",
      "tags": ["moon", "moon-guides"],
      "title": "Supplements that actually matter",
      "subtitle": "Skip the hype. Here’s what the research supports.",
      "readTimeMinutes": 5,
      "body": {
        "introduction": "Evidence-based",
        "quickSummary": [
          {
            "title": "Start early.",
            "description": "Most supplements need 2–3 months to have an effect. Begin well before your cycle."
          },
          {
            "title": "Quality over quantity.",
            "description": "Focus on the 4–5 with real evidence rather than a dozen unproven ones."
          },
          {
            "title": "Talk to your doctor first.",
            "description": "Supplements can interact with medications and some are only appropriate for specific diagnoses."
          }
        ],
        "sections": [
          {
            "title": "CoQ10 (Ubiquinol)",
            "subtitle": "600mg/day, strongest evidence for egg quality",
            "content": "CoQ10 has the strongest evidence for egg quality improvement. It supports mitochondrial function in eggs, which is critical for proper cell division. Start at least 2–3 months before your cycle to give it time to work."
          },
          {
            "title": "Prenatal with methylfolate",
            "content": "A prenatal vitamin with methylfolate (not synthetic folic acid) is essential. Methylfolate is the bioavailable form that your body can use directly, and about 30–40% of people have MTHFR gene variants that make it harder to convert folic acid. Folate is crucial for preventing neural tube defects and supporting early embryo development."
          },
          {
            "title": "Vitamin D",
            "subtitle": "2,000–4,000 IU daily",
            "content": "Vitamin D plays a role in hormone regulation and implantation. Studies show that women with sufficient vitamin D levels have higher pregnancy rates with IVF. Get your level tested: many people are deficient, especially in northern climates."
          },
          {
            "title": "Omega-3 fatty acids",
            "subtitle": "DHA + EPA, at least 1,000mg combined",
            "content": "Omega-3s support hormone production and reduce inflammation. They’re also critical for fetal brain development once you’re pregnant. Choose a high-quality fish oil or algae-based supplement."
          },
          {
            "title": "DHEA",
            "subtitle": "25mg three times daily, under doctor supervision only",
            "content": "DHEA is sometimes recommended for women with diminished ovarian reserve. The evidence is mixed but some studies show it may improve egg yield and quality. Only take this under your doctor’s supervision, as it’s a hormone precursor."
          },
          {
            "title": "What to skip",
            "content": "Most supplements marketed for “fertility” have minimal evidence. Royal jelly, maca root, and fertility-branded blends are largely unproven. Focus on the basics above. More supplements doesn’t mean better outcomes. It just means more pills and more money."
          }
        ],
        "references": [
          {
            "authors": "Ben-Meir et al.",
            "title": "Coenzyme Q10 restores oocyte mitochondrial function and fertility during reproductive aging. Aging Cell (2015)"
          },
          {
            "authors": "Gaskins & Chavarro",
            "title": "Diet and fertility: a review. American Journal of Obstetrics and Gynecology (2018)"
          }
        ],
        "relatedArticles": ["moon-article-cycle-process"]
      }
    },
    {
      "id": "moon-article-lab-grown-eggs",
      "kind": "article",
      "tags": ["moon", "moon-spotlight"],
      "title": "Lab-grown eggs: the breakthrough rewriting fertility",
      "subtitle": "Scientists turned skin cells into viable eggs. What it means for you.",
      "readTimeMinutes": 5,
      "body": {
        "introduction": "Spotlight",
        "quickSummary": [
          {
            "title": "Skin cells to eggs.",
            "description": "Researchers have successfully created functional egg cells from mouse skin cells using iPSC technology."
          },
          {
            "title": "10–15 years away.",
            "description": "Most experts estimate clinical availability is still a decade or more out for humans."
          },
          {
            "title": "Not available yet.",
            "description": "This technology is still in early research stages and not available for clinical use."
          }
        ],
        "sections": [
          {
            "title": "The Kyushu breakthrough",
            "content": "In a landmark 2024 study, researchers at Kyushu University in Japan successfully created functional egg cells from mouse skin cells using induced pluripotent stem cell (iPSC) technology. The resulting eggs were fertilized and produced healthy, fertile offspring."
          },
          {
            "title": "What is in vitro gametogenesis (IVG)?",
            "content": "This technique, called in vitro gametogenesis (IVG), could eventually allow anyone to produce eggs from a simple skin or blood sample, regardless of age or ovarian function. For fertility patients, it would mean an essentially unlimited egg supply without hormonal stimulation or surgical retrieval."
          },
          {
            "title": "The race to human application",
            "content": "Several biotech companies are now racing to translate this from mice to humans. Conception Bio, a Bay Area startup, announced in early 2025 that they had created the first human egg cell from stem cells, though it has not yet been fertilized or tested for viability."
          },
          {
            "title": "Timeline & hurdles",
            "content": "The timeline for clinical use is uncertain: most experts estimate 10–15 years before human IVG could be available in clinics. Significant hurdles remain: ensuring genetic safety, navigating regulatory approval, and addressing the profound ethical questions this technology raises."
          },
          {
            "title": "What this means for you now",
            "content": "Probably not much for your current treatment decisions. But it’s a sign that the science of fertility is advancing rapidly, and options that seem like science fiction today may be routine within a generation."
          },
          {
            "title": "Stick with proven technology",
            "content": "In the meantime, the best approach is still to work with proven, established technologies (egg freezing, embryo cryopreservation, and IVF) while keeping an eye on these exciting developments."
          }
        ],
        "references": [
          {
            "authors": "Ishikura et al.",
            "title": "In vitro reconstitution of the whole male germ-cell development from mouse pluripotent stem cells. Cell Stem Cell (2021)"
          },
          {
            "authors": "Yamashiro et al.",
            "title": "Generation of human oogonia from induced pluripotent stem cells in vitro. Science (2018)"
          }
        ]
      }
    },
    {
      "id": "moon-article-eggs-vs-embryos",
      "kind": "article",
      "tags": ["moon", "moon-decisions"],
      "title": "Eggs vs embryos: which should you freeze?",
      "subtitle": "The trade-offs between freezing eggs alone and creating embryos.",
      "readTimeMinutes": 5,
      "body": {
        "introduction": "Decisions",
        "quickSummary": [
          {
            "title": "Eggs = flexibility.",
            "description": "Frozen eggs don’t require a partner or donor now, and you choose how to use them later."
          },
          {
            "title": "Embryos = certainty.",
            "description": "You already know fertilization worked. Higher thaw survival rates and the option for genetic testing."
          },
          {
            "title": "No wrong answer.",
            "description": "The best choice depends on your relationship, age, and how much uncertainty you’re comfortable with."
          }
        ],
        "sections": [
          {
            "title": "The core difference",
            "content": "Both paths start the same way: ovarian stimulation, monitoring, and egg retrieval. The difference is what happens after retrieval. With egg freezing, your mature eggs are vitrified immediately. With embryo freezing, eggs are fertilized with sperm first, cultured for 5–7 days, and the resulting embryos are frozen."
          },
          {
            "title": "Survival rates after thawing",
            "content": "Embryos have a slight edge: 95%+ survival after thaw vs 85–90% for eggs. This is because embryos are multicellular and more resilient to the vitrification process. In practical terms, if you freeze 12 eggs, you might expect 10–11 to survive thawing. If those same eggs had been fertilized and 8 became embryos, nearly all 8 would survive thawing."
          },
          {
            "title": "Fertilization is a filter",
            "content": "When you create embryos, you learn right away whether fertilization works. Not all eggs fertilize, and not all fertilized eggs develop to blastocyst. With frozen eggs, you won’t know this until years later when you thaw and use them. For some people, removing this uncertainty is the biggest advantage of embryo freezing."
          },
          {
            "title": "Genetic testing (PGT-A)",
            "content": "Only embryos can be tested for chromosomal abnormalities before transfer. PGT-A screens each embryo for the right number of chromosomes, which helps select the one most likely to lead to a healthy pregnancy. This is especially relevant after age 35, when the rate of chromosomal abnormalities rises."
          },
          {
            "title": "The flexibility argument",
            "content": "Frozen eggs are entirely yours. There are no legal complications if your relationship changes. You can decide later whether to use a partner’s sperm, donor sperm, or not use them at all. Embryos, by contrast, are legally shared between the egg and sperm providers: you’ll need a legal agreement covering ownership, use, and disposition."
          },
          {
            "title": "When eggs make more sense",
            "content": "You’re single or not sure about your current partner. You want maximum flexibility for the future. You’re under 35 and have a good expected egg yield. You want to avoid the legal complexity of shared embryos.",
            "bulletPoints": [
              "Single or uncertain about partner",
              "Want maximum future flexibility",
              "Under 35 with good ovarian reserve",
              "Prefer to avoid legal agreements"
            ]
          },
          {
            "title": "When embryos make more sense",
            "content": "You have a committed partner. You want the highest possible success rates per frozen unit. You’re over 35 and want to maximize your chances. You want the option to test embryos genetically.",
            "bulletPoints": [
              "Committed partner or chosen donor",
              "Want the certainty of knowing fertilization worked",
              "Over 35 or concerned about egg quality",
              "Interested in PGT-A genetic testing"
            ]
          },
          {
            "title": "Can you do both?",
            "content": "Yes. Some patients freeze a portion of their eggs and fertilize the rest to create embryos. This hedge approach gives you both flexibility and certainty. Discuss with your RE whether your expected yield makes this practical."
          }
        ],
        "references": [
          {
            "authors": "Practice Committees of ASRM and SART",
            "title": "Mature oocyte cryopreservation: a guideline. Fertility and Sterility (2013)"
          },
          {
            "authors": "Cobo et al.",
            "title": "Oocyte vitrification as an efficient option for elective fertility preservation. Fertility and Sterility (2016)"
          }
        ],
        "relatedArticles": [
          "moon-article-embryo-freezing",
          "moon-article-fresh-vs-frozen-choice",
          "moon-article-pgt-testing"
        ]
      }
    }
  ],
  "relationships": [],
  "localeVariants": []
}
